import { Empty, Pagination, PaginationProps, Spin } from 'antd';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  Grid,
  GridLabels,
  GridRow,
} from '../../../../../../common/components/Grid/Grid';
import styles from './MyLeadsContactsTable.module.css';
import { CONTACTS_TABLE_CELL_DATA } from './render-contacts-table-cells';
import { useResolutionInfo } from '@mm/hooks';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { connectedPageSelector } from '@mm/state/selectors/viewSelectors';
import { ContactSlideoutMenuProps } from '../../Audiences/ContactSlideoutMenu/ContactSlideoutMenu';
import { Contact } from '@mm/state/interfaces/contacts';
import { ContactDeleteModal } from '../../../../contacts/components/ContactDeleteModal/ContactDeleteModal';

import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useCreateMyLeadsAudience } from '../use-create-my-leads-audience';
import { SelectAllLeadsButton } from '../SelectAllLeadsButton/SelectAllLeadsButton';
import { MOBILE_SMALL } from '../../../../../constants/screenSize';
import {
  MyLeadsCreateAudienceRequestParams,
  MyLeadsSearchQuery,
} from '@mm/state/interfaces/myLeads';
import {
  selectMyLeadsContacts,
  selectMyLeadsPagination,
} from '@mm/state/selectors/myLeadsSelectors';
import { useMyLeadsCRUDActions } from '@mm/state/actions/MyLeadsCRUDActions';

type Props = {
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
  isFetchingContacts?: boolean;
};

// tslint:disable-next-line: cyclomatic-complexity
export const MyLeadsContactsTable = ({
  filterParams,
  setFilterParams,
  isFetchingContacts,
}: Props) => {
  const connectedPage = useSelector(connectedPageSelector);
  const contacts = useSelector(selectMyLeadsContacts) || [];
  const pagination = useSelector(selectMyLeadsPagination);
  const { total: totalContacts, perPage, page } = pagination || {};

  const hasLeads = totalContacts > 0;

  const [selectedCheckboxes, setSelectedCheckboxes] = useState<number[]>([]);
  const [excludedLeadIds, setExcludedLeadIds] = useState<number[]>([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedLeadsCount, setSelectedLeadsCount] = useState<
    number | undefined
  >(undefined);
  const noSelectedLeads = !selectedCheckboxes.length && !selectedLeadsCount;
  const [contactForDeletion, setContactForDeletion] = useState<Contact>();
  const [busy, setBusy] = useState(isFetchingContacts || false);
  const isFetching = busy || isFetchingContacts;

  const contactsCRUDActions = useContactsCRUDActions();
  const { searchLeads } = useMyLeadsCRUDActions();
  const { showSlideoutMenu, hideSlideoutMenu } = useViewActions();
  const { openCreateMyLeadsAudienceModal } = useModalActions();
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;

  const selectAllHandler = (isAllSelected: boolean, leadsCount: number) => {
    setSelectedAll(isAllSelected);
    setSelectedCheckboxes([]);
    setExcludedLeadIds([]);
    setSelectedLeadsCount(leadsCount);
  };

  const { createMyLeadsAudience } = useCreateMyLeadsAudience(() =>
    selectAllHandler(false, 0),
  );

  const GRID_TEMPLATE_COLUMNS = isMobile
    ? '1fr'
    : '1fr 2fr 1fr 1fr 0.75fr 0.25fr';

  const invalidSelectedLeadsCount =
    !selectedLeadsCount || selectedLeadsCount - excludedLeadIds.length === 0;

  const isCheckboxChecked = (contactId: number, idx: number) => {
    const index = (page - 1) * perPage + idx;
    if (selectedAll) {
      return (
        !excludedLeadIds.some(checkboxId => checkboxId === contactId) &&
        index < Number(selectedLeadsCount)
      );
    }
    return selectedCheckboxes.some(checkboxId => checkboxId === contactId);
  };

  const isCheckboxDisabled = (idx: number) => {
    const index = (page - 1) * perPage + idx;
    if (selectedAll) {
      return index >= (selectedLeadsCount || 0);
    }
  };

  const onCheckboxClick = (contactId: number, idx: number) => {
    const isExisting = isCheckboxChecked(contactId, idx);

    if (selectedAll) {
      if (isExisting) {
        setExcludedLeadIds(current => [...current, contactId]);
      } else {
        setExcludedLeadIds(current => [
          ...current.filter(checkboxId => checkboxId !== contactId),
        ]);
      }
      return;
    }

    if (isExisting) {
      setSelectedCheckboxes(current => [
        ...current.filter(checkboxId => checkboxId !== contactId),
      ]);
      setSelectedLeadsCount(prev => ((prev || 0) > 0 ? (prev || 0) - 1 : 0));
    } else {
      setSelectedCheckboxes(current => [...current, contactId]);
      setSelectedLeadsCount(prev => (prev || 0) + 1);
    }
  };

  const getUniqueIds = (values: number[]) => {
    return [...new Set(values)];
  };

  const onCreateAudienceClick = async ({
    withAddToAutomation,
    withExport,
  }: {
    withAddToAutomation?: boolean;
    withExport?: boolean;
  }) => {
    const uniqCheckboxes = getUniqueIds([...selectedCheckboxes]);

    const params = (
      selectedAll
        ? {
            query: filterParams.query,
            exclude: filterParams.exclude,
            limit: selectedLeadsCount,
            excludedLeadIds,
          }
        : {
            leadIds: uniqCheckboxes,
          }
    ) as MyLeadsCreateAudienceRequestParams;

    if (withAddToAutomation || withExport) {
      createMyLeadsAudience({
        params,
        withAddToAutomation,
        withExport,
      });
      return;
    }

    openCreateMyLeadsAudienceModal({ params });
  };

  const onPaginationChange: PaginationProps['onChange'] = async page => {
    const updatedParams = {
      ...filterParams,
      page,
    };

    setBusy(true);
    setFilterParams(updatedParams);
    setBusy(false);
  };

  const deleteSelectedContact = async (
    contactId: number,
    eraseRemoteMessages,
  ) => {
    if (connectedPage) {
      await contactsCRUDActions.deleteSelectedContact(
        connectedPage.id,
        contactId,
        pagination?.page,
        pagination?.perPage,
        undefined,
        eraseRemoteMessages,
      );

      await searchLeads(filterParams, true);
      hideSlideoutMenu();
    }
  };

  const getSelectedContact = (contactId: number) => {
    openContactSlideoutMenu({
      contactId,
      deleteSelectedContact,
      onContactSelect: getSelectedContact,
      onContactUpdateSuccess: async () => {
        await searchLeads(filterParams, true);
      },
    });
  };

  const openContactSlideoutMenu = useCallback(
    (props: ContactSlideoutMenuProps) => {
      showSlideoutMenu('contact', props);
    },
    [],
  );

  useEffect(() => {
    setSelectedCheckboxes([]);
    setSelectedLeadsCount(undefined);
    setSelectedAll(false);
  }, [
    filterParams.query?.attributes,
    filterParams.query?.variables,
    filterParams.exclude,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.bar}>
        {Boolean(totalContacts) && (
          <div className={styles.leadsCount}>
            {totalContacts.toLocaleString('en-US')} filtered
          </div>
        )}

        <SelectAllLeadsButton
          disabled={!hasLeads}
          maxLeadsCount={totalContacts}
          count={Number(selectedLeadsCount) - excludedLeadIds.length}
          setCount={setSelectedLeadsCount}
          onSelectAllClick={() => selectAllHandler(true, totalContacts)}
          onReset={() => selectAllHandler(false, 0)}
        />

        <div className={styles.buttonsArea}>
          <PrimaryButton
            className={styles.primaryButton}
            disabled={!hasLeads || noSelectedLeads || invalidSelectedLeadsCount}
            onClick={() => onCreateAudienceClick({})}
          >
            Build an Audience
          </PrimaryButton>
          <PrimaryButton
            className={styles.primaryButton}
            disabled={!hasLeads || noSelectedLeads || invalidSelectedLeadsCount}
            onClick={() => onCreateAudienceClick({ withAddToAutomation: true })}
          >
            Add to Automation
          </PrimaryButton>
          <PrimaryButton
            className={styles.primaryButton}
            disabled={!hasLeads || noSelectedLeads || invalidSelectedLeadsCount}
            onClick={() => onCreateAudienceClick({ withExport: true })}
          >
            Export
          </PrimaryButton>
        </div>
      </div>
      <div className={styles.gridWrapper}>
        <Grid className={styles.grid}>
          <GridLabels
            className={classNames(styles.gridLabels, {
              [styles.gridLabelsEmptyState]: !hasLeads,
            })}
            gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
          >
            {CONTACTS_TABLE_CELL_DATA.map(item => {
              const isFirstNameColumns = item.key === 'firstName';
              return (
                <div
                  className={classNames(styles.tableHeader, {
                    [styles.firstNameHeading]: isFirstNameColumns,
                  })}
                  key={item.key}
                >
                  {item.label}
                </div>
              );
            })}
          </GridLabels>

          <div
            className={classNames(styles.gridRows, {
              [styles.gridRowsDisabled]: isFetching,
            })}
          >
            <Spin
              tip="Retrieving contacts..."
              spinning={isFetching}
              className={styles.spin}
            >
              {contacts?.map((contact, idx) => {
                const isChecked = isCheckboxChecked(contact.id, idx);
                const isDisabled = isCheckboxDisabled(idx);

                return (
                  <GridRow
                    key={contact.id}
                    className={classNames(styles.gridRow, {
                      [styles.gridRowSelected]: isChecked,
                      [styles.disabledGridRow]: isDisabled,
                    })}
                    gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
                    onClick={() => getSelectedContact(contact.id)}
                  >
                    {CONTACTS_TABLE_CELL_DATA.map(({ Cell, key }) => {
                      return Cell({
                        contact,
                        key,
                        onClick: e => {
                          if (key === 'fullName') {
                            e?.stopPropagation();
                            onCheckboxClick(contact.id, idx);
                          }
                          if (key === 'action') {
                            setContactForDeletion(contact);
                          }
                        },
                        isChecked,
                        disabled: isDisabled,
                      });
                    })}
                  </GridRow>
                );
              })}
            </Spin>
          </div>
        </Grid>
        <ContactDeleteModal
          showEraseRemoteMessagesOption={
            !!contactForDeletion?.remoteId.startsWith('em_')
          }
          show={!!contactForDeletion}
          emailAddress={contactForDeletion?.email || ''}
          onDelete={async eraseRemoteMessages => {
            if (contactForDeletion) {
              await deleteSelectedContact(
                contactForDeletion.id,
                eraseRemoteMessages,
              );
            }
          }}
          onClose={() => setContactForDeletion(undefined)}
        />
      </div>
      {!!hasLeads && (
        <div className={styles.paginationWrapper}>
          <Pagination
            current={page}
            onChange={onPaginationChange}
            pageSize={perPage}
            showSizeChanger={false}
            total={totalContacts}
            disabled={isFetching}
            hideOnSinglePage
          />
        </div>
      )}
      {!hasLeads && !isFetching && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Contacts" />
      )}
    </div>
  );
};
